<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Profil</h1>
        <p class="page-subtitle" v-if="business">{{ business.name }}</p>
      </div>
      <v-btn color="primary" depressed small @click="openEditModal" v-if="business" :loading="loading">
        <v-icon left small>mdi-pencil</v-icon>
        Modifier
      </v-btn>
    </div>

    <div v-if="loading" class="page-loader">
      <v-progress-circular indeterminate color="primary" size="40" width="3"></v-progress-circular>
    </div>

    <div v-if="business && !loading">
      <!-- Logo + Info -->
      <v-row>
        <v-col cols="12" md="4">
          <v-card flat class="detail-card">
            <v-card-text class="pa-6 text-center">
              <v-avatar v-if="business.logo_path" size="100" class="mb-3">
                <v-img :src="getLogoUrl(business.logo_path)" contain></v-img>
              </v-avatar>
              <v-avatar v-else size="100" color="grey lighten-3" class="mb-3">
                <v-icon size="48" color="grey">mdi-image</v-icon>
              </v-avatar>
              <h3 class="text-body-1 font-weight-bold">{{ business.name }}</h3>
              <p class="text-caption text--secondary mb-4">{{ business.email }}</p>
              <div class="d-flex justify-center" style="gap: 8px;">
                <v-btn x-small outlined color="primary" @click="openLogoDialog">
                  <v-icon left x-small>mdi-upload</v-icon>
                  {{ business.logo_path ? 'Changer' : 'Ajouter logo' }}
                </v-btn>
                <v-btn v-if="business.logo_path" x-small outlined color="error" @click="removeLogo" :loading="logoLoading">
                  <v-icon x-small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="8">
          <v-card flat class="detail-card">
            <v-card-title class="detail-card-title">
              <v-icon color="primary" size="18" class="mr-2">mdi-domain</v-icon>
              Informations
            </v-card-title>
            <v-card-text class="pa-0">
              <v-simple-table dense>
                <tbody>
                  <tr v-for="item in profile" :key="item.key">
                    <td class="text-caption font-weight-bold text--secondary" style="width: 180px;">{{ item.label }}</td>
                    <td>{{ item.name || '—' }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Edit modal -->
    <v-dialog v-model="editModal" max-width="640" @click:outside="cancelEdit">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="primary" size="18" class="mr-2">mdi-pencil</v-icon>
          Modifier les informations
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body" style="max-height: 60vh; overflow-y: auto;">
          <v-form ref="form" v-model="formValid">
            <div v-for="item in profile" :key="item.key" class="form-group">
              <label class="form-label">{{ item.label }}</label>
              <v-textarea v-if="item.key === 'receipt_message'" v-model="formData[item.key]" outlined dense rows="3" hide-details></v-textarea>
              <v-select v-else-if="item.key === 'contact_id_type'" v-model="formData[item.key]" :items="id_types" item-text="label" item-value="name" outlined dense hide-details></v-select>
              <v-text-field v-else v-model="formData[item.key]" outlined dense hide-details :disabled="item.key === 'name'"></v-text-field>
            </div>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="cancelEdit">Annuler</v-btn>
          <v-btn color="primary" depressed :loading="submitLoading" @click="submitUpdate">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Logo upload dialog -->
    <v-dialog v-model="logoDialog" max-width="440" @click:outside="cancelLogoUpload">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="primary" size="18" class="mr-2">mdi-upload</v-icon>
          {{ business && business.logo_path ? 'Changer le logo' : 'Ajouter un logo' }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <v-file-input v-model="logoFile" label="Sélectionner une image" accept="image/*" outlined dense hide-details @change="onLogoFileChange"></v-file-input>
          <div v-if="logoPreview" class="text-center mt-4">
            <v-img :src="logoPreview" max-width="160" max-height="160" contain class="mx-auto rounded elevation-1"></v-img>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="cancelLogoUpload">Annuler</v-btn>
          <v-btn color="primary" depressed :loading="logoUploadLoading" :disabled="!logoFile" @click="uploadLogo">Téléverser</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false"><v-icon small>mdi-close</v-icon></v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'

export default {
  name: 'AccountView',
  components: { BreadcrumbComponent },
  data() {
    return {
      items: [{ text: 'Profil', disabled: true, href: '/account' }],
      loading: true, business: null, profile: [], formData: {}, originalFormData: {}, formValid: false,
      editModal: false, submitLoading: false,
      logoDialog: false, logoFile: null, logoPreview: null, logoLoading: false, logoUploadLoading: false,
      id_types: [
        { name: 'CIN', label: "Carte d'identité nationale" }, { name: 'NIF', label: "Numéro d'identification fiscale" },
        { name: 'NINU', label: "Numéro d'identification nationale unique" }, { name: 'PASSPORT', label: 'Passeport' },
        { name: 'DRIVER_LICENSE', label: 'Permis de conduire' }
      ],
      snackbar: { show: false, text: '', color: 'success', timeout: 3000 },
    }
  },
  computed: {
    hasChanges() { return JSON.stringify(this.formData) !== JSON.stringify(this.originalFormData) }
  },
  methods: {
    showSnackbar(text, color) { this.snackbar = { show: true, text: text, color: color || 'success', timeout: 3000 } },
    getLogoUrl(p) { if (!p) return null; return p.startsWith('http') ? p : 'https://keslakay.s3.us-east-1.amazonaws.com/' + p },
    async load() {
      try {
        this.loading = true
        var auth = JSON.parse(localStorage.authentication || '{}')
        var r = await this.$axios.get('/businesses?include=stores,devices,renewals&prefix=' + auth.schema)
        if (!r.data || !r.data.length) return
        this.business = r.data[0]
        this.profile = [
          { label: 'Entreprise', name: this.business.name, key: 'name' },
          { label: 'Téléphone', name: this.business.phone, key: 'phone' },
          { label: 'E-mail', name: this.business.email, key: 'email' },
          { label: 'Adresse', name: this.business.address, key: 'address' },
          { label: 'Personne de contact', name: this.business.contact_full_name, key: 'contact_full_name' },
          { label: 'Identification', name: this.business.contact_id, key: 'contact_id' },
          { label: "Type d'identification", name: this.business.contact_id_type, key: 'contact_id_type' },
          { label: 'Message du reçu', name: this.business.receipt_message, key: 'receipt_message' }
        ]
        this.formData = {}; this.profile.forEach(function (i) { this.formData[i.key] = i.name || '' }.bind(this))
        this.originalFormData = JSON.parse(JSON.stringify(this.formData))
      } catch (e) { console.error(e) }
      finally { this.loading = false }
    },
    openEditModal() { this.editModal = true; this.formData = JSON.parse(JSON.stringify(this.originalFormData)) },
    cancelEdit() { this.editModal = false },
    async submitUpdate() {
      this.submitLoading = true
      try {
        await this.$axios.put('/businesses/' + this.business.id + '?data=' + JSON.stringify(this.formData))
        this.editModal = false; this.showSnackbar('Informations mises à jour'); await this.load()
      } catch (e) { this.showSnackbar('Erreur', 'error') }
      this.submitLoading = false
    },
    openLogoDialog() { this.logoDialog = true; this.logoFile = null; this.logoPreview = null },
    cancelLogoUpload() { this.logoDialog = false; this.logoFile = null; this.logoPreview = null },
    onLogoFileChange(file) { if (file) { var r = new FileReader(); r.onload = function (e) { this.logoPreview = e.target.result }.bind(this); r.readAsDataURL(file) } else { this.logoPreview = null } },
    async uploadLogo() {
      if (!this.logoFile) return
      this.logoUploadLoading = true
      try {
        var fd = new FormData(); fd.append('logo', this.logoFile)
        var auth = JSON.parse(localStorage.authentication || '{}'); if (auth.schema) fd.append('solgc_schema', auth.schema)
        var r = await this.$axios.post('/businesses/upload_logo/' + this.business.id, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
        if (r.data) { this.business.logo_path = r.data.logo_path; this.logoDialog = false; this.showSnackbar('Logo téléversé'); this.updateBusinessInLocalStorage() }
      } catch (e) { this.showSnackbar('Erreur', 'error') }
      this.logoUploadLoading = false
    },
    async removeLogo() {
      if (!confirm('Supprimer le logo ?')) return
      this.logoLoading = true
      try { await this.$axios.post('/businesses/remove_logo/' + this.business.id); this.business.logo_path = null; this.showSnackbar('Logo supprimé'); this.updateBusinessInLocalStorage() }
      catch (e) { this.showSnackbar('Erreur', 'error') }
      this.logoLoading = false
    },
    updateBusinessInLocalStorage() { localStorage.setItem('business', JSON.stringify(this.business)); window.dispatchEvent(new CustomEvent('businessUpdated', { detail: { business: this.business } })) },
  },
  created() { this.load() },
}
</script>

<style scoped>
.detail-card { border: 1px solid #e8ecf1 !important; border-radius: 10px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.detail-card-title { font-size: 0.875rem !important; font-weight: 600 !important; padding: 14px 16px !important; color: #1a2332; border-bottom: 1px solid #f1f5f9; }
.detail-card .v-simple-table tbody td { font-size: 0.8125rem; padding: 10px 16px !important; border-bottom: 1px solid #f1f5f9 !important; }
.detail-card .v-simple-table tbody tr:last-child td { border-bottom: none !important; }
.dialog-card { border-radius: 12px !important; }
.dialog-title { font-size: 0.9375rem !important; font-weight: 600 !important; padding: 14px 20px 12px !important; }
.dialog-body { padding: 20px 20px 8px !important; }
.dialog-actions { padding: 8px 16px 12px !important; }
.form-group { margin-bottom: 16px; }
.form-label { display: block; font-size: 0.75rem; font-weight: 600; color: #475569; margin-bottom: 4px; }
</style>
